/**
 * The Dispatch Planner's "Open TT" column, shared by all three planner grids
 * (Imports / Exports / Drop Released).
 *
 * Mirrors the At Risk Monitor's TT column — same badge, same hover list, click
 * opens the pro — but the backend only ships tickets that are still open here
 * (see `attach_open_trouble_tickets` in app/utils/service_failure_tt.py), so a
 * badge means "someone still has to act on this" rather than "this pro has
 * ticket history". Resolved and cancelled tickets are filtered out server-side.
 *
 * Consequences of that server-side filter, both intentional:
 *  - the badge is always red, since `TroubleTicketBadge` reddens on any
 *    NEW/PENDING ticket and every listed ticket is one;
 *  - the count is typically far lower than the ARM TT column's for the same
 *    pro (prod 2026-09-02: 45 badged pros on the /imports set vs ARM's 271).
 *
 * Built as a factory rather than a module-level literal because each grid needs
 * its own `onOpen` binding. Callers should memoize the result so the columns
 * arrays it feeds keep a stable identity — a rebuilt columns array costs
 * in-progress driver-cell focus and validation in the planner.
 */
import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { TroubleTicketBadge, type TroubleTicketEntry } from '../../components/TroubleTicketBadge';

/** The row shape this column reads. Both fields are attached by the backend's
 *  imports/exports/dropreleased listings and are absent elsewhere. */
export interface OpenTtRow {
  pronumber: number;
  openTtCount?: number;
  openTickets?: TroubleTicketEntry[];
  /** Leg sub-rows repeat their parent's order-level data; see the cell below. */
  isSubRow?: boolean;
}

export function buildOpenTtColumn<T extends OpenTtRow>(
  onOpen: (pronumber: number) => void,
): ColumnDef<T> {
  return {
    id: 'openTt',
    accessorFn: (row) => row.openTtCount ?? 0,
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: 'arrIncludes',
    header: 'Open TT',
    size: 80,
    cell: ({ row }) => {
      // Tickets are order-level, and a sub-row carries a copy of its parent's
      // order fields — rendering there would duplicate the badge down the group.
      if (row.original.isSubRow) return null;
      return (
        <TroubleTicketBadge
          tickets={row.original.openTickets || []}
          count={row.original.openTtCount || 0}
          onClick={() => onOpen(row.original.pronumber)}
          placement="right"
        />
      );
    },
    // Grouped rows borrow their first leaf's `row.original`, which would render
    // one arbitrary member's badge on the group header.
    aggregatedCell: () => null,
  };
}
